/**
 * Estado do app do Mapa da Produção (GET /api/mapa-producao/estado), lido a
 * cada 15 s enquanto a aba está visível — é o que faz o mapa "andar" sozinho
 * quando outro gestor aponta algo no tablet. Traz também o DESENHO: cada
 * setor, subsetor e posto vem com a própria `forma` (`forma.js`).
 *
 * - Aba oculta: não pergunta nada; ao voltar, lê na hora.
 * - Erro: mantém o último estado bom e preenche `erro` (a tela avisa sem
 *   apagar o mapa).
 * - `mudancas`: o que mudou de status/posto/subsetor desde a leitura
 *   anterior, para as telas animarem só o que andou.
 * - `aplicarAlocacao`/`aplicarTarefa`/`aplicarPeca`/`aplicarSetor`: a resposta
 *   de uma escrita entra na hora, sem esperar o próximo ciclo; uma leitura que
 *   já estava em voo (feita antes da escrita) é descartada para não desfazer o
 *   que o gestor acabou de ver mudar.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../../../context/AuthContext";
import { obterEstadoMapa } from "../../../services/mapaProducao";

export const INTERVALO_ESTADO_MS = 15000;

const COLECOES = ["setores", "subsetores", "postos", "colaboradores", "alocacoes", "tarefas", "pecas"];
const CAMPOS_ALOCACAO = ["setor_id", "subsetor_id", "posto_id", "status"];
const CAMPOS_TAREFA = ["setor_id", "subsetor_id", "posto_id", "colaborador_id", "status"];
const CAMPOS_PECA = ["setor_id", "subsetor_id", "posto_id", "colaborador_id", "status"];
const SEM_MUDANCAS = Object.freeze([]);

export const ESTADO_VAZIO = Object.freeze({
  gerado_em: null,
  setores: [],
  subsetores: [],
  postos: [],
  colaboradores: [],
  alocacoes: [],
  tarefas: [],
  pecas: [],
});

function normalizarEstado(bruto) {
  const estado = { gerado_em: bruto?.gerado_em || null };
  COLECOES.forEach((colecao) => {
    estado[colecao] = Array.isArray(bruto?.[colecao]) ? bruto[colecao] : [];
  });
  return estado;
}

// gerado_em muda a cada leitura; o resto só quando alguém apontou algo.
function mesmoConteudo(a, b) {
  return COLECOES.every((colecao) => JSON.stringify(a[colecao]) === JSON.stringify(b[colecao]));
}

function diferencas(alvo, anteriores, atuais, chaveDe, campos) {
  const antes = new Map(anteriores.map((item) => [chaveDe(item), item]));
  return atuais.reduce((lista, item) => {
    const velho = antes.get(chaveDe(item));
    const alterados = velho ? campos.filter((campo) => velho[campo] !== item[campo]) : [];
    if (velho && alterados.length === 0) return lista;
    lista.push({ alvo, id: chaveDe(item), nova: !velho, campos: alterados, antes: velho || null, depois: item });
    return lista;
  }, []);
}

/**
 * [{alvo: "ambiente"|"tarefa"|"peca", id (chave do ambiente | id da tarefa |
 *   id da peça), nova, campos: [...campos que mudaram], antes, depois}].
 * Sem leitura anterior (primeira carga), nada "mudou".
 */
export function detectarMudancas(anterior, atual) {
  if (!anterior || !atual) return SEM_MUDANCAS;
  const lista = [
    ...diferencas("ambiente", anterior.alocacoes, atual.alocacoes, (a) => a.chave, CAMPOS_ALOCACAO),
    ...diferencas("tarefa", anterior.tarefas, atual.tarefas, (t) => t.id, CAMPOS_TAREFA),
    ...diferencas("peca", anterior.pecas, atual.pecas, (p) => p.id, CAMPOS_PECA),
  ];
  return lista.length ? lista : SEM_MUDANCAS;
}

function substituirOuIncluir(lista, item, chaveDe) {
  const chave = chaveDe(item);
  const indice = lista.findIndex((atual) => chaveDe(atual) === chave);
  if (indice < 0) return [...lista, item];
  const copia = [...lista];
  copia[indice] = item;
  return copia;
}

const paginaOculta = () => typeof document !== "undefined" && document.visibilityState === "hidden";

const INICIAL = { estado: ESTADO_VAZIO, carregando: true, erro: null, atualizadoEm: null, mudancas: SEM_MUDANCAS };

function useLeituraDoEstado(request) {
  const [situacao, setSituacao] = useState(INICIAL);
  const estadoRef = useRef(null); // último estado aplicado (null = nunca leu)
  const geracaoRef = useRef(0);
  const emVooRef = useRef(null);
  const requestRef = useRef(request);
  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const aplicar = useCallback((novo, atualizadoEm) => {
    const anterior = estadoRef.current;
    const igual = anterior && mesmoConteudo(anterior, novo);
    const estado = igual ? anterior : novo;
    estadoRef.current = estado;
    setSituacao({
      estado,
      carregando: false,
      erro: null,
      atualizadoEm,
      mudancas: igual ? SEM_MUDANCAS : detectarMudancas(anterior, novo),
    });
  }, []);

  const buscar = useCallback(({ forcar = false } = {}) => {
    if (emVooRef.current && !forcar) return emVooRef.current;
    geracaoRef.current += 1;
    const minhaGeracao = geracaoRef.current;
    // Promise.resolve: um erro síncrono (sem sessão) vira rejeição tratada.
    const promessa = Promise.resolve()
      .then(() => obterEstadoMapa(requestRef.current))
      .then((bruto) => {
        if (minhaGeracao !== geracaoRef.current) return;
        const novo = normalizarEstado(bruto);
        aplicar(novo, novo.gerado_em || new Date().toISOString());
      })
      .catch((e) => {
        if (minhaGeracao !== geracaoRef.current) return;
        const mensagem = e?.message || "Não foi possível atualizar o mapa.";
        setSituacao((s) => ({ ...s, carregando: false, erro: mensagem, mudancas: SEM_MUDANCAS }));
      })
      .finally(() => {
        if (emVooRef.current === promessa) emVooRef.current = null;
      });
    emVooRef.current = promessa;
    return promessa;
  }, [aplicar]);

  // Resposta de escrita: entra agora e invalida a leitura em voo.
  const aplicarItem = useCallback((colecao, item, chaveDe) => {
    if (!item) return;
    // Sem a primeira leitura não há base para mesclar: lê tudo de novo.
    if (!estadoRef.current) {
      buscar({ forcar: true });
      return;
    }
    geracaoRef.current += 1;
    emVooRef.current = null;
    const base = estadoRef.current;
    aplicar({ ...base, [colecao]: substituirOuIncluir(base[colecao], item, chaveDe) }, new Date().toISOString());
  }, [aplicar, buscar]);

  return { situacao, buscar, aplicarItem };
}

function usePolling(buscar, intervaloMs, ativo) {
  useEffect(() => {
    if (!ativo) return undefined;
    // A PRIMEIRA leitura acontece mesmo com a aba oculta: sem ela a tela
    // nasce vazia ("Conectando…", nenhuma peça) quando é aberta em segundo
    // plano ou com a janela minimizada. Só o intervalo poupa a aba oculta.
    buscar();
    const lerSeVisivel = () => {
      if (!paginaOculta()) buscar();
    };
    const intervalo = setInterval(lerSeVisivel, intervaloMs);
    document.addEventListener("visibilitychange", lerSeVisivel);
    return () => {
      clearInterval(intervalo);
      document.removeEventListener("visibilitychange", lerSeVisivel);
    };
  }, [buscar, intervaloMs, ativo]);
}

/**
 * useEstadoMapa({request?, intervaloMs?, ativo?}) →
 *   {estado, carregando, erro, recarregar, atualizadoEm, mudancas,
 *    aplicarAlocacao, aplicarTarefa, aplicarPeca, aplicarSetor}
 *
 * `request` vem do useAuth por padrão (injetável nos testes). `ativo: false`
 * desliga o polling (ex.: sem permissão de ver).
 */
export default function useEstadoMapa({ request, intervaloMs = INTERVALO_ESTADO_MS, ativo = true } = {}) {
  const auth = useAuth();
  const { situacao, buscar, aplicarItem } = useLeituraDoEstado(request || auth?.request);
  usePolling(buscar, intervaloMs, ativo);

  const recarregar = useCallback(() => buscar({ forcar: true }), [buscar]);
  const aplicarAlocacao = useCallback(
    (alocacao) => aplicarItem("alocacoes", alocacao, (a) => a.chave),
    [aplicarItem]
  );
  const aplicarTarefa = useCallback((tarefa) => aplicarItem("tarefas", tarefa, (t) => t.id), [aplicarItem]);
  const aplicarPeca = useCallback((peca) => aplicarItem("pecas", peca, (p) => p.id), [aplicarItem]);
  const aplicarSetor = useCallback((setor) => aplicarItem("setores", setor, (s) => s.id), [aplicarItem]);

  return { ...situacao, recarregar, aplicarAlocacao, aplicarTarefa, aplicarPeca, aplicarSetor };
}
